import { useCallback, useEffect, useRef } from "react";
import { useAuthSession } from "./useAuthSession";
import { ProjectsApi } from "@/services/api/projects";
import { useEditorStore } from "@/stores/editor/editor-store";
import { logger } from "@/utils";
import type { ProjectSnapshot, ProjectVersion } from "@/types";
import { ProjectVersionSchema } from "@/schemas/versioning";
import {
  buildPersistedProjectState,
  buildPersistedPageSnapshotsWithBindingScopeRepair,
  buildPersistedProjectStateWithBindingScopeRepair,
} from "@/services/persistence/persistedProjectState";

interface UseProjectAutoSaveOptions {
  projectId: string;
  autoSaveInterval?: number; // in milliseconds
  enabled?: boolean;
  onSaveSuccess?: (timestamp: Date) => void;
  onSaveError?: (error: Error) => void;
}

interface ProjectSaveState {
  isSaving: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
  saveError: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export const useProjectAutoSave = ({
  projectId,
  autoSaveInterval = 30000, // 30 seconds default
  enabled = true,
  onSaveSuccess,
  onSaveError,
}: UseProjectAutoSaveOptions) => {
  const { extendSession, isAuthenticated } = useAuthSession();
  const {
    components,
    selectedComponents,
    viewport,
    zoom,
    gridEnabled,
    snapToGrid,
    showGuides,
    activePanel,
    canvasSettings,
    pages,
    currentPageId,
    getAllPagesWithComponents,
    syncCurrentPageComponents,
  } = useEditorStore();

  // Save state
  const saveStateRef = useRef<ProjectSaveState>({
    isSaving: false,
    lastSaved: null,
    hasUnsavedChanges: false,
    saveError: null,
  });

  // Track changes
  const lastSavedComponentsRef = useRef<string>("");
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSaveTimeRef = useRef<number>(0); // Track last save time for throttling
  const hasInitializedProjectBaselineRef = useRef(false);
  const MIN_SAVE_INTERVAL = 10000; // Minimum 10 seconds between saves

  // Stable refs for callbacks that change often — prevents dep chain avalanche
  const onSaveSuccessRef = useRef(onSaveSuccess);
  const onSaveErrorRef = useRef(onSaveError);
  onSaveSuccessRef.current = onSaveSuccess;
  onSaveErrorRef.current = onSaveError;

  // Refs for beforeunload handler values (synced cheaply, avoid re-registering)
  const projectIdRef = useRef(projectId);
  const selectedComponentsRef = useRef(selectedComponents);
  const currentPageIdRef = useRef(currentPageId);
  const viewportRef = useRef(viewport);
  const zoomRef = useRef(zoom);
  const gridEnabledRef = useRef(gridEnabled);
  const snapToGridRef = useRef(snapToGrid);
  const showGuidesRef = useRef(showGuides);
  const activePanelRef = useRef(activePanel);
  const canvasSettingsRef = useRef(canvasSettings);
  const getAllPagesRef = useRef(getAllPagesWithComponents);
  const syncPageRef = useRef(syncCurrentPageComponents);
  const currentProjectRef = useRef(useEditorStore.getState().project);

  // Cheap sync effect — no dep array casualties
  useEffect(() => {
    const store = useEditorStore.getState();

    projectIdRef.current = projectId;
    selectedComponentsRef.current = selectedComponents;
    currentPageIdRef.current = currentPageId;
    viewportRef.current = viewport;
    zoomRef.current = zoom;
    gridEnabledRef.current = gridEnabled;
    snapToGridRef.current = snapToGrid;
    showGuidesRef.current = showGuides;
    activePanelRef.current = activePanel;
    canvasSettingsRef.current = canvasSettings;
    getAllPagesRef.current = getAllPagesWithComponents;
    syncPageRef.current = syncCurrentPageComponents;
    currentProjectRef.current = store.project;
  });

  const buildCurrentVersionSnapshot = useCallback(async (): Promise<ProjectSnapshot> => {
    const store = useEditorStore.getState();
    const currentProject = store.project;

    if (!currentProject) {
      throw new Error("No project loaded");
    }

    const persistedPages = await buildPersistedPageSnapshotsWithBindingScopeRepair(
      getAllPagesWithComponents(),
    );

    return {
      pages: persistedPages,
      navigation: currentProject.navigation,
      globalSettings: currentProject.settings,
      metadata: {
        totalComponents: persistedPages.reduce(
          (total, page) =>
            total + page.components.length,
          0,
        ),
        totalPages: persistedPages.length,
        lastEditedPageId: store.currentPageId,
        complexity: "moderate",
        estimatedLoadTime: 0,
        optimizationHints: [],
      },
    };
  }, [getAllPagesWithComponents]);

  const appendVersionFromResponse = useCallback(
    (rawData: unknown, snapshot: ProjectSnapshot) => {
      const newVersionEntry = isRecord(rawData)
        ? rawData.newVersionEntry
        : undefined;

      const parsedVersion = ProjectVersionSchema.safeParse({
        ...(newVersionEntry &&
        typeof newVersionEntry === "object" &&
        !Array.isArray(newVersionEntry)
          ? newVersionEntry
          : {}),
        snapshot,
      });

      if (!parsedVersion.success) {
        return;
      }

      const versionToAppend: ProjectVersion = {
        ...parsedVersion.data,
        snapshot,
      };

      useEditorStore.getState().appendProjectVersion(versionToAppend);
    },
    [],
  );

  /**
   * Save project manually.
   * No longer calls validateSession() — the API client interceptor at
   * client.ts:184 already handles 401s with re-auth modal + request queuing.
   */
  const saveProject = useCallback(
    async (caller: string = "unknown", snapshotName?: string): Promise<boolean> => {
      if (!enabled || saveStateRef.current.isSaving || !isAuthenticated) {
        return false;
      }

      try {
        saveStateRef.current.isSaving = true;
        saveStateRef.current.saveError = null;

        // Cancel any pending auto-save when doing a manual save
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
          saveTimeoutRef.current = null;
        }

        // Extend user session
        extendSession();

        // Sync current page components before saving
        syncCurrentPageComponents();
        const versionSnapshot = await buildCurrentVersionSnapshot();

        const currentProject = useEditorStore.getState().project;
        if (!currentProject) {
          logger.warn("Skipping manual save because no project is loaded", {
            projectId,
            caller,
          });
          return false;
        }
        const pagesWithComponents = getAllPagesWithComponents();

        const editorSettings: Record<string, unknown> = {
          viewport: viewport || null,
          zoom: typeof zoom === "number" ? zoom : 1,
          gridEnabled: Boolean(gridEnabled),
          snapToGrid: Boolean(snapToGrid),
          showGuides: Boolean(showGuides),
          activePanel: activePanel || "components",
          canvasSettings: canvasSettings ? { ...canvasSettings } : {},
        };
        const saveData = await buildPersistedProjectStateWithBindingScopeRepair({
          pagesWithComponents,
          currentPageId,
          schemaVersion: currentProject.schemaVersion,
          lastModified: new Date(),
          snapshotName,
          settings: editorSettings,
          metadata: {
            selectedComponents: Array.isArray(selectedComponents)
              ? [...selectedComponents]
              : [],
            currentPageId: currentPageId || null,
            totalPages: pagesWithComponents.length,
            editorState: {
              viewport: viewport || null,
              zoom: typeof zoom === "number" ? zoom : 1,
              gridEnabled: Boolean(gridEnabled),
              snapToGrid: Boolean(snapToGrid),
              showGuides: Boolean(showGuides),
              activePanel: activePanel || "components",
              canvasSettings: canvasSettings ? { ...canvasSettings } : {},
            },
          },
        });
        const persistedPages = saveData.pages;
        if (persistedPages.length === 0) {
          logger.warn("Skipping manual save because no pages are loaded", {
            projectId,
            caller,
          });
          return false;
        }

        // Debug logging to see what we're sending
        logger.debug("Project save data:", {
          projectId,
          pagesCount: persistedPages.length,
          pages: persistedPages.map((page) => ({
            pageId: page.pageId,
            pageName: page.pageName,
            componentsCount: page.components.length,
          })),
          saveData: {
            ...saveData,
            pages: persistedPages.map((page) => ({
              ...page,
              components: `${page.components.length} components`,
            })),
          },
        });

        const response = await ProjectsApi.saveProject(projectId, saveData);

        if (response.success) {
          const now = new Date();
          saveStateRef.current.lastSaved = now;
          saveStateRef.current.hasUnsavedChanges = false;

          // Update last saved state
          const savedState = {
            components,
            pages,
            selectedComponents,
            currentPageId,
            viewport,
            zoom,
            gridEnabled,
            snapToGrid,
            showGuides,
            activePanel,
            canvasSettings,
          };
          lastSavedComponentsRef.current = JSON.stringify(savedState);

          // Track last save time for throttling (prevents auto-save conflicts)
          lastSaveTimeRef.current = Date.now();

          onSaveSuccessRef.current?.(now);

          appendVersionFromResponse(response.data, versionSnapshot);

          logger.info("Project saved successfully", {
            projectId,
            timestamp: now,
          });

          return true;
        } else {
          console.error(
            "❌ [SAVE] Save failed - server response not successful:",
            {
              projectId,
              caller,
              response,
            },
          );
          throw new Error("Save failed: Server response not successful");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown save error";
        console.error("❌ [SAVE] Save error:", {
          projectId,
          caller,
          error: errorMessage,
          errorObject: error,
        });
        saveStateRef.current.saveError = errorMessage;

        onSaveErrorRef.current?.(error instanceof Error ? error : new Error(errorMessage));
        logger.error("Failed to save project:", { projectId, error });

        return false;
      } finally {
        saveStateRef.current.isSaving = false;
      }
    },
    [
      enabled,
      projectId,
      components,
      selectedComponents,
      pages,
      currentPageId,
      viewport,
      zoom,
      gridEnabled,
      snapToGrid,
      showGuides,
      activePanel,
      canvasSettings,
      extendSession,
      isAuthenticated,
      syncCurrentPageComponents,
      getAllPagesWithComponents,
      buildCurrentVersionSnapshot,
      appendVersionFromResponse,
    ],
  );

  /**
   * Auto-save with debouncing and throttling
   */
  const triggerAutoSave = useCallback(() => {
    if (!enabled) {
      return;
    }

    // Throttle: Don't allow saves more frequently than MIN_SAVE_INTERVAL
    const now = Date.now();
    const timeSinceLastSave = now - lastSaveTimeRef.current;

    if (timeSinceLastSave < MIN_SAVE_INTERVAL) {
      // Schedule for later instead of resetting debounce
      if (!saveTimeoutRef.current) {
        const waitTime = MIN_SAVE_INTERVAL - timeSinceLastSave;
        saveTimeoutRef.current = setTimeout(() => {
          saveTimeoutRef.current = null;
          if (saveStateRef.current.hasUnsavedChanges && !saveStateRef.current.isSaving) {
            triggerAutoSave();
          }
        }, waitTime);
      }
      return; // Don't reset debounce, just wait
    }

    // If already saving, retry after a short delay instead of skipping
    if (saveStateRef.current.isSaving) {
      if (!saveTimeoutRef.current) {
        saveTimeoutRef.current = setTimeout(() => {
          saveTimeoutRef.current = null;
          if (saveStateRef.current.hasUnsavedChanges && !saveStateRef.current.isSaving) {
            triggerAutoSave();
          }
        }, 2000);
      }
      return;
    }

    // Clear existing timeout before setting new one
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // Set new timeout for debounced save
    saveTimeoutRef.current = setTimeout(() => {
      if (saveStateRef.current.hasUnsavedChanges) {
        // Sync current page components before auto-save
        syncCurrentPageComponents();
        void (async () => {
          try {
            const versionSnapshot = await buildCurrentVersionSnapshot();
            const currentProject = useEditorStore.getState().project;
            if (!currentProject) {
              logger.warn("Skipping auto-save because no project is loaded", {
                projectId,
              });
              return;
            }

            const saveData = await buildPersistedProjectStateWithBindingScopeRepair({
              pagesWithComponents: getAllPagesRef.current(),
              currentPageId,
              schemaVersion: currentProject.schemaVersion,
              lastModified: new Date(),
              metadata: {
                selectedComponents: Array.isArray(selectedComponents)
                  ? [...selectedComponents]
                  : [],
                currentPageId: currentPageId || null,
                editorState: {
                  viewport: viewport || null,
                  zoom: typeof zoom === "number" ? zoom : 1,
                  gridEnabled: Boolean(gridEnabled),
                  snapToGrid: Boolean(snapToGrid),
                  showGuides: Boolean(showGuides),
                  activePanel: activePanel || "components",
                  canvasSettings: canvasSettings ? { ...canvasSettings } : {},
                },
              },
            });

            if (saveData.pages.length === 0) {
              logger.warn("Skipping auto-save because no pages are loaded", {
                projectId,
              });
              return;
            }

            const response = await ProjectsApi.autoSaveProject(projectId, saveData);
            appendVersionFromResponse(response.data, versionSnapshot);
            lastSaveTimeRef.current = Date.now();
            saveStateRef.current.hasUnsavedChanges = false;
          } catch (error) {
            logger.warn("Auto-save failed:", { projectId, error });
          }
        })();
      }
      saveTimeoutRef.current = null;
    }, autoSaveInterval);
  }, [
    autoSaveInterval,
    enabled,
    projectId,
    selectedComponents,
    currentPageId,
    viewport,
    zoom,
    gridEnabled,
    snapToGrid,
    showGuides,
    activePanel,
    canvasSettings,
    syncCurrentPageComponents,
    buildCurrentVersionSnapshot,
    appendVersionFromResponse,
  ]);

  /**
   * Check for changes - only track actual content changes, not UI state
   * This prevents excessive saves when user is just selecting/zooming/etc.
   */
  const checkForChanges = useCallback(() => {
    // Only serialize actual content that needs to be saved
    // Excluding: selectedComponents, viewport, zoom, gridEnabled, snapToGrid,
    //            showGuides, activePanel (these are UI state, not content)
    const currentState = JSON.stringify({
      components,
      pages,
      currentPageId,
    });

    const hasLoadedProjectState = pages.length > 0 || components.length > 0;
    if (hasLoadedProjectState && !hasInitializedProjectBaselineRef.current) {
      lastSavedComponentsRef.current = currentState;
      hasInitializedProjectBaselineRef.current = true;
      saveStateRef.current.hasUnsavedChanges = false;
      return;
    }

    const hasChanges = currentState !== lastSavedComponentsRef.current;

    if (hasChanges !== saveStateRef.current.hasUnsavedChanges) {
      saveStateRef.current.hasUnsavedChanges = hasChanges;

      if (hasChanges) {
        triggerAutoSave();
      }
    }
  }, [
    components,
    pages,
    currentPageId,
    triggerAutoSave,
  ]);

  /**
   * Setup auto-save - debounced onChange only, no periodic interval
   * The throttle in triggerAutoSave ensures we don't save more than once per MIN_SAVE_INTERVAL
   */
  useEffect(() => {
    if (!enabled) return;

    // Check for changes on every content update
    checkForChanges();

    // Cleanup timeout on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [enabled, checkForChanges]);

  /**
   * Save before page unload — uses refs so the effect only re-registers
   * when projectId changes, not on every store update.
   */
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (saveStateRef.current.hasUnsavedChanges) {
        const message =
          "You have unsaved changes. Are you sure you want to leave?";
        event.returnValue = message;
        return message;
      }
      return;
    };

    const handleUnload = () => {
      if (saveStateRef.current.hasUnsavedChanges) {
        // Sync current page components before unload save
        syncPageRef.current();

        const pagesWithComponents = getAllPagesRef.current();

        if (pagesWithComponents.length === 0) {
          return;
        }

        const payload = buildPersistedProjectState({
          pagesWithComponents,
          currentPageId: currentPageIdRef.current,
          schemaVersion: currentProjectRef.current?.schemaVersion,
          lastModified: new Date(),
          metadata: {
            selectedComponents: Array.isArray(selectedComponentsRef.current)
              ? [...selectedComponentsRef.current]
              : [],
            currentPageId: currentPageIdRef.current || null,
            editorState: {
              viewport: viewportRef.current || null,
              zoom: typeof zoomRef.current === "number" ? zoomRef.current : 1,
              gridEnabled: Boolean(gridEnabledRef.current),
              snapToGrid: Boolean(snapToGridRef.current),
              showGuides: Boolean(showGuidesRef.current),
              activePanel: activePanelRef.current || "components",
              canvasSettings: canvasSettingsRef.current ? { ...canvasSettingsRef.current } : {},
            },
          },
        });

        navigator.sendBeacon(
          `/api/projects/${projectIdRef.current}/autosave`,
          JSON.stringify(payload),
        );
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, [projectId]);

  /**
   * Handle visibility change (save when tab becomes hidden)
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "hidden" &&
        saveStateRef.current.hasUnsavedChanges
      ) {
        // For visibility change, we do want to save immediately for safety
        // but let's add a small delay to avoid conflicts
        setTimeout(() => {
          if (
            saveStateRef.current.hasUnsavedChanges &&
            !saveStateRef.current.isSaving
          ) {
            saveProject("visibility-change");
          }
        }, 100);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [saveProject]);

  /**
   * Cancel any pending autosave and reset the "last saved" baseline so the
   * change-detection loop does not immediately re-queue an autosave.
   * Call this after a version restore so the restored state is treated as
   * the new baseline rather than as unsaved changes.
   */
  const cancelAndResetAutoSave = useCallback(() => {
    // 1. Kill any in-flight debounce timer
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // 2. Snapshot current store state as the new baseline
    const store = useEditorStore.getState();
    lastSavedComponentsRef.current = JSON.stringify({
      components: store.components,
      pages: store.pages,
      currentPageId: store.currentPageId,
    });

    // 3. Clear the unsaved-changes flag
    saveStateRef.current.hasUnsavedChanges = false;
    hasInitializedProjectBaselineRef.current = true;

    // 4. Push the throttle window forward so auto-save can't fire for another full interval
    lastSaveTimeRef.current = Date.now();
  }, []);

  return {
    // State
    isSaving: saveStateRef.current.isSaving,
    lastSaved: saveStateRef.current.lastSaved,
    hasUnsavedChanges: saveStateRef.current.hasUnsavedChanges,
    saveError: saveStateRef.current.saveError,

    // Actions
    saveProject,
    cancelAndResetAutoSave,

    // Utilities
    getSaveStatus: () => ({
      isSaving: saveStateRef.current.isSaving,
      lastSaved: saveStateRef.current.lastSaved,
      hasUnsavedChanges: saveStateRef.current.hasUnsavedChanges,
      saveError: saveStateRef.current.saveError,
    }),
  };
};

export default useProjectAutoSave;
